import CloseIcon from "@mui/icons-material/Close";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CButton from "../../components/CButton";
import CDialog from "../../components/CDialog";
import { Slider } from "@mui/material";
import { POPULATION_GROUPS, TRAINING_CATEGORY_OPTIONS } from "../../constant/constants";
import {
  fetchBBBEEBudgetConfiguration,
  fetchCompanyUserDemographics,
} from "../../slices/b_bbeeConfigurationSlice";
import { fetchYearId } from "../../slices/yearSlice";
import { fetchTrainingCategoryBudgetConfiguration } from "../../slices/trainingCategoryBudgetSlice";
import { fetchTotalBudget } from "../../slices/totalBudgetSlice";
import { toast } from "react-toastify";
import {
  buildPlanOptimizerPayload,
  fetchTrainingCategoryDefaults,
  hasPlanOptimizerEligibleTrainingData,
} from "../../utils/planOptimizerService";
import {
  runPlanOptimizerV2Generate,
  savePlanOptimizerV2Artifact,
  listSetaFieldNamesFromTemplate,
} from "../../utils/planOptimizerV2Service";
import { recomputeAllocationSummary } from "../../utils/planOptimizerAllocationSummary";
import CLoader from "../../components/CLoader";
import { computeAutoAlignedSliderValue } from "../../utils/planOptimizerAutoAlign";
import AllocationSummaryModal from "./AllocationSummaryModal";
import { getLocalData, getTrainingCategoryBudgetUsage, formatZAR } from "../../constant/utils/commonUtils";
import { BBEE_bifurcation, fetchDepartmentBudgetDetailsDisabled, fetchDepartmentBudgetDetails, trainingCategoryBudgetConfig as fetchTrainingCategoryBudgetConfig } from "../../slices/dashboardSlice";
import { getDemographicsBudgetData } from "../../constant/utils/demographicProcessor";
import { trainingFormStatus } from "../../constant/constants";
import { fetchTrainingCategoryConfig } from "../../slices/trainingCategoryConfigSlice";
import { fetchConfiguration } from "../../slices/configSlice";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import {
  hasUnemployedTypesConfigured,
  computeUnemployedRecommendedInvestment,
  excludeUnemployedFromAllocated,
} from "../../constant/utils/dashboardUnemployedUtils";
import { supabase } from "../../config/supabaseClient";

const AutoAlignButton = ({ onClick, disabled }) => (
  <CButton
    type="button"
    onClick={onClick}
    disabled={disabled}
    tabIndex={-1}
    className="!text-[10px] !leading-tight !min-w-[4.5rem] !h-auto !px-1.5 !py-1 whitespace-nowrap shrink-0 bg-white text-blue-500 hover:bg-blue-50 border border-blue-300 rounded disabled:opacity-40 disabled:cursor-not-allowed"
  >
    Auto-Align
  </CButton>
);

/** Amount + slider + T stacked; Auto-Align vertically centered on the right. */
const SLIDER_SX = {
  "& .MuiSlider-thumb": {
    width: 14,
    height: 14,
    backgroundColor: "#ffffff",
    border: "2px solid #aae3ff",
    boxShadow: "none",
    "&:hover, &.Mui-focusVisible, &.Mui-active": { boxShadow: "none" },
  },
  "& .MuiSlider-track": {
    height: 3,
    backgroundColor: "#008cd1",
    border: "none",
  },
  "& .MuiSlider-rail": {
    height: 3,
    backgroundColor: "#aae3ff",
    opacity: 1,
  },
  padding: "6px 0",
};

const BudgetControlCell = ({
  value,
  onChange,
  max,
  targetDisplay,
  showCurrencyPrefix = true,
  disabled = false,
  tabIndex,
  onAutoAlign,
  autoAlignDisabled = false,
  showAutoAlign = true,
}) => (
  <div className="plan-optimizer-budget-control flex flex-row items-center gap-2 w-full min-w-0 px-1 py-0.5 text-left">
    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
      <div className="flex items-center gap-1 min-w-0">
        <span
          className="text-xs text-gray-700 shrink-0 cursor-help"
          title="Current: The current/adjusted value you are setting"
        >
          C:
        </span>
        <div className="relative shrink-0">
          {showCurrencyPrefix && (
            <span className="pointer-events-none absolute left-1.5 top-1/2 z-[10] -translate-y-1/2 text-xs text-gray-700">
              R
            </span>
          )}
          <input
            type="number"
            tabIndex={tabIndex}
            disabled={disabled}
            value={Number(value) || 0}
            onChange={(e) => {
              const raw = e.target.value;
              onChange(
                showCurrencyPrefix
                  ? parseFloat(raw) || 0
                  : parseInt(raw, 10) || 0,
              );
            }}
            className={`plan-optimizer-amount-input text-xs text-center border border-blue-200 rounded px-1 py-0.5 font-semibold text-gray-700 focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              showCurrencyPrefix ? "pl-4" : ""
            }`}
          />
        </div>
      </div>
      <div className="plan-optimizer-slider-wrap w-full min-w-0">
        <Slider
          tabIndex={-1}
          disabled={disabled}
          value={Number(value) || 0}
          onChange={(e, newValue) => onChange(newValue)}
          min={0}
          max={max || 500}
          valueLabelDisplay="off"
          className="w-full"
          sx={SLIDER_SX}
        />
      </div>
      <div className="flex items-center gap-1 min-w-0">
        <span
          className="text-xs text-gray-700 shrink-0 cursor-help"
          title="Target: The target value from budget configuration"
        >
          T:
        </span>
        <span className="text-xs font-semibold text-gray-700 truncate">
          {targetDisplay}
        </span>
      </div>
    </div>
    {showAutoAlign ? (
      <div className="plan-optimizer-auto-align shrink-0 self-center">
        <AutoAlignButton
          onClick={onAutoAlign}
          disabled={autoAlignDisabled || disabled}
        />
      </div>
    ) : null}
  </div>
);

const PlanOptimizerModal = ({ open, onClose, onGenerate, initialValues = null }) => {
  const dispatch = useDispatch();
  const {
    demographics,
    budgetConfiguration,
    loading: bbeeConfigLoading,
  } = useSelector((state) => state.bBbeeConfiguration);
  const {
    budgetConfiguration: trainingCategoryBudgetConfig,
    loading: trainingCategoryBudgetConfigLoading,
  } = useSelector((state) => state.trainingCategoryBudget);
  const { totalBudget, loading: totalBudgetLoading } = useSelector(
    (state) => state.totalBudget,
  );
  const configData = useSelector((state) => state.config.configData);
  const setaTemplate = useSelector((state) => state.setaTemplate.setaTemplate);
  const employmentTypes = useSelector(
    (state) => state.setaTemplate.employmentTypes || [],
  );

  // BBEE type is always "Budget" (radio buttons hidden)
  const [bbeeType] = useState("Budget");
  const [trainingCategoryType, setTrainingCategoryType] = useState(initialValues?.trainingCategoryType || "Budget");
  const [criticalSkillsPriority, setCriticalSkillsPriority] = useState(
    initialValues?.criticalSkillsPriority ?? 5,
  );
  const [yearId, setYearId] = useState(null);
  const [bbeeValuesInitialized, setBbeeValuesInitialized] = useState(false);
  const [trainingCategoryValuesInitialized, setTrainingCategoryValuesInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatedPlanData, setGeneratedPlanData] = useState(null);
  const [isAllocationSummaryModalOpen, setIsAllocationSummaryModalOpen] = useState(false);
  const [optimizedPayloadData, setOptimizedPayloadData] = useState(null);
  const [trainingCategoryDefaults, setTrainingCategoryDefaults] = useState({});
  const [trainingCategoryDefaultsLoaded, setTrainingCategoryDefaultsLoaded] = useState(false);
  const [allocationSummaryData, setAllocationSummaryData] = useState(null);
  const [completePlanArtifact, setCompletePlanArtifact] = useState(null);
  const [precomputedSummary, setPrecomputedSummary] = useState(null);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [bbeeBifurcationLoaded, setBbeeBifurcationLoaded] = useState(false);
  const [trainingCategoryBudgetLoaded, setTrainingCategoryBudgetLoaded] = useState(false);
  const [yearIdResolved, setYearIdResolved] = useState(false);
  const [configurationWarnings, setConfigurationWarnings] = useState([]);
  // State for Disability (separate from other groups, total only — NOT in Current Plan)
  const [disabilityData, setDisabilityData] = useState(() => {
    if (initialValues?.disabilityData) {
      return initialValues.disabilityData;
    }
    return {
      total: "0",
    };
  });
  // Unemployed: single total (no M/F); IS part of BBEE Current Plan
  const [unemployedData, setUnemployedData] = useState(() => {
    if (initialValues?.unemployedData) {
      return initialValues.unemployedData;
    }
    return { c: 0, t: 0 };
  });
  const showUnemployedSlider = hasUnemployedTypesConfigured(employmentTypes);
  const companyId = JSON.parse(localStorage.getItem("sdfUser"))?.company_id;

  // Use TRAINING_CATEGORY_OPTIONS from constants
  const trainingCategories = TRAINING_CATEGORY_OPTIONS;

  // State for BBEE values: { [groupKey]: { male: { c: 0, t: 0 }, female: { c: 0, t: 0 } } }
  const [bbeeValues, setBbeeValues] = useState(() => {
    if (initialValues?.bbeeValues) {
      return initialValues.bbeeValues;
    }
    const initial = {};
    POPULATION_GROUPS.forEach((group) => {
      initial[group.key] = {
        male: { c: 0, t: 0 },
        female: { c: 0, t: 0 },
      };
    });
    return initial;
  });

  // State for Training Category values: { [categoryName]: { c: 200, t: 200 } }
  const [trainingCategoryValues, setTrainingCategoryValues] = useState(() => {
    if (initialValues?.trainingCategoryValues) {
      // Convert old format (male/female) to new format (single value) if needed
      const converted = {};
      Object.keys(initialValues.trainingCategoryValues).forEach((key) => {
        const value = initialValues.trainingCategoryValues[key];
        const defaultVal = typeof trainingCategoryDefaults[key] === 'number'
          ? trainingCategoryDefaults[key]
          : 0;
        if (value.male && value.female) {
          // Old format - convert to new format (use male values or sum)
          converted[key] = {
            c: value.male?.c || value.female?.c || defaultVal,
            t: value.male?.t || value.female?.t || defaultVal,
          };
        } else {
          // New format - use as is
          converted[key] = value;
        }
      });
      return converted;
    }
    // Initialize training category values - start with 0, will be populated from database budget values when available
    const initialValuesObj = {};
    TRAINING_CATEGORY_OPTIONS.forEach((category) => {
      initialValuesObj[category.value] = {
        c: 0,
        t: 0,
      };
    });
    return initialValuesObj;
  });

  // Update Training Category T: values from DB configuration
  // Only sets T values, C values will be loaded from Dashboard function
  useEffect(() => {
    if (trainingCategoryBudgetConfig && Array.isArray(trainingCategoryBudgetConfig) && trainingCategoryBudgetConfig.length > 0 && totalBudget) {
      setTrainingCategoryValues((prevValues) => {
        const updatedValues = { ...prevValues };
        const isFirstLoad = !trainingCategoryValuesInitialized;

        TRAINING_CATEGORY_OPTIONS.forEach((category) => {
          const existingConfig = trainingCategoryBudgetConfig.find(
            (item) => item.trainingType === category.value
          );

          if (existingConfig) {
            // Use budget value for T: (already calculated from percentage)
            const tValue = Number(existingConfig.budget) || 0;
            const prevT = prevValues[category.value]?.t;
            
            // Only update T value if it has changed
            // C value will be set by fetchAndPopulateTrainingCategoryBudgetData
            if (prevT !== tValue || !prevValues[category.value]) {
              updatedValues[category.value] = {
                c: isFirstLoad ? 0 : (prevValues[category.value]?.c ?? 0),
                t: tValue,
              };
            }
          } else {
            // No config found - use budget value directly from database defaults for T only
            const budgetValue = typeof trainingCategoryDefaults[category.value] === 'number'
              ? trainingCategoryDefaults[category.value]
              : 0;

            // Only set T value if not already set, keep C as is
            if (!prevValues[category.value]?.t && budgetValue > 0) {
              updatedValues[category.value] = {
                c: isFirstLoad ? 0 : (prevValues[category.value]?.c ?? 0),
                t: budgetValue,
              };
            }
          }
        });

        // Mark as initialized after first load
        if (isFirstLoad) {
          setTrainingCategoryValuesInitialized(true);
        }

        return updatedValues;
      });
    }
  }, [trainingCategoryBudgetConfig, trainingCategoryValuesInitialized, totalBudget, trainingCategoryDefaults]);

  // When no training_category_budget_config exists, set T: from training_category_config defaults only (never C:)
  useEffect(() => {
    if (
      !totalBudget ||
      trainingCategoryValuesInitialized ||
      !trainingCategoryDefaultsLoaded ||
      (trainingCategoryBudgetConfig && Array.isArray(trainingCategoryBudgetConfig) && trainingCategoryBudgetConfig.length > 0)
    ) {
      return;
    }

    setTrainingCategoryValues((prevValues) => {
      const updatedValues = { ...prevValues };
      let hasChanges = false;

      TRAINING_CATEGORY_OPTIONS.forEach((category) => {
        const budgetValue = typeof trainingCategoryDefaults[category.value] === 'number'
          ? trainingCategoryDefaults[category.value]
          : null;

        if (budgetValue !== null && budgetValue > 0 && !prevValues[category.value]?.t) {
          updatedValues[category.value] = {
            c: 0,
            t: budgetValue,
          };
          hasChanges = true;
        }
      });

      return hasChanges ? updatedValues : prevValues;
    });
    setTrainingCategoryValuesInitialized(true);
  }, [
    totalBudget,
    trainingCategoryBudgetConfig,
    trainingCategoryValuesInitialized,
    trainingCategoryDefaults,
    trainingCategoryDefaultsLoaded,
  ]);

  const isInitialLoading =
    open &&
  (
    !eligibilityChecked ||
    !bbeeValuesInitialized ||
    !trainingCategoryValuesInitialized ||
    !bbeeBifurcationLoaded ||
    !trainingCategoryBudgetLoaded
  );

  // Reset init flags when modal opens so C: values reload from live allocation data
  useEffect(() => {
    if (open) {
      setBbeeValuesInitialized(false);
      setTrainingCategoryValuesInitialized(false);
      setTrainingCategoryDefaultsLoaded(false);
      setEligibilityChecked(false);
      setBbeeBifurcationLoaded(false);
      setTrainingCategoryBudgetLoaded(false);
      setYearIdResolved(false);
      setConfigurationWarnings([]);
      // Disability C starts at 0 (like BBEE); planned spend is loaded separately. T stays from config.
      setDisabilityData({ total: "0" });
    }
  }, [open]);

  // Verify training data eligibility when modal opens (was blocking popup open on Optimize click)
  useEffect(() => {
    if (!open || !companyId) return;

    let cancelled = false;
    (async () => {
      const hasTraining = await hasPlanOptimizerEligibleTrainingData(companyId);
      if (cancelled) return;
      if (!hasTraining) {
        toast.info("No training data is available for this plan.");
        onClose();
        return;
      }
      setEligibilityChecked(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  // Fetch yearId and data when modal opens
  useEffect(() => {
    if (open && companyId) {
      dispatch(fetchYearId())
        .then((res) => {
          const fetchedYearId = res.payload;
          setYearId(fetchedYearId);
          if (fetchedYearId) {
            dispatch(fetchBBBEEBudgetConfiguration({ yearId: fetchedYearId, companyId }));
            dispatch(fetchCompanyUserDemographics(companyId));
            dispatch(fetchTrainingCategoryBudgetConfiguration({ yearId: fetchedYearId, companyId }));
            dispatch(fetchTotalBudget({ yearId: fetchedYearId, companyId }));
            fetchTrainingCategoryDefaults(companyId, fetchedYearId)
              .then((defaults) => {
                setTrainingCategoryDefaults(defaults);
              })
              .finally(() => {
                setTrainingCategoryDefaultsLoaded(true);
              });
          } else {
            setTrainingCategoryDefaultsLoaded(true);
          }
        })
        .finally(() => {
          setYearIdResolved(true);
        });
    }
  }, [open, companyId, dispatch]);

  // Ensure company config and SETA template are available for allocation lookups
  useEffect(() => {
    if (!open || !companyId || !yearId) return;
    if (!configData) {
      dispatch(fetchConfiguration({ yearId, companyId }));
    }
    if (!setaTemplate?.length) {
      dispatch(fetchSetaTemplateByCompanyId(companyId));
    }
  }, [open, companyId, yearId, configData, setaTemplate, dispatch]);

  // Mark BBEE sliders ready when year or demographic budget config is unavailable
  useEffect(() => {
    if (!open || !eligibilityChecked || !yearIdResolved || bbeeValuesInitialized) {
      return;
    }
    if (!yearId) {
      setBbeeValuesInitialized(true);
      return;
    }
    if (bbeeConfigLoading || totalBudgetLoading) {
      return;
    }
    if (budgetConfiguration?.demographic_config?.config && totalBudget) {
      return;
    }
    setBbeeValuesInitialized(true);
  }, [
    open,
    eligibilityChecked,
    yearIdResolved,
    bbeeValuesInitialized,
    yearId,
    bbeeConfigLoading,
    totalBudgetLoading,
    budgetConfiguration,
    totalBudget,
  ]);

  // Mark training category sliders ready when config cannot be loaded
  useEffect(() => {
    if (!open || !eligibilityChecked || !yearIdResolved || trainingCategoryValuesInitialized) {
      return;
    }
    if (
      !trainingCategoryDefaultsLoaded ||
      trainingCategoryBudgetConfigLoading ||
      totalBudgetLoading
    ) {
      return;
    }
    if (
      trainingCategoryBudgetConfig &&
      Array.isArray(trainingCategoryBudgetConfig) &&
      trainingCategoryBudgetConfig.length > 0 &&
      totalBudget
    ) {
      return;
    }
    setTrainingCategoryValuesInitialized(true);
  }, [
    open,
    eligibilityChecked,
    yearIdResolved,
    trainingCategoryValuesInitialized,
    trainingCategoryDefaultsLoaded,
    trainingCategoryBudgetConfigLoading,
    totalBudgetLoading,
    trainingCategoryBudgetConfig,
    totalBudget,
  ]);

  const buildConfigurationWarnings = useCallback(() => {
    const warnings = [];

    if (!yearId) {
      warnings.push("No active planning year is configured.");
    }

    if ((Number(totalBudget) || 0) <= 0) {
      warnings.push(
        "Total training budget is not configured or is zero. Generate is disabled until a budget greater than zero is set.",
      );
    }

    if (!budgetConfiguration?.demographic_config?.config?.length) {
      warnings.push(
        "B-BBEE demographic budget configuration is not set up. Target values will default to zero.",
      );
    }

    const hasTrainingCategoryTargets =
      (Array.isArray(trainingCategoryBudgetConfig) &&
        trainingCategoryBudgetConfig.length > 0) ||
      Object.values(trainingCategoryDefaults).some(
        (value) => typeof value === "number" && value > 0,
      );

    if (!hasTrainingCategoryTargets) {
      warnings.push(
        "Training category budget configuration is not set up. Target values will default to zero.",
      );
    }

    if (!setaTemplate?.length) {
      warnings.push(
        "SETA template is not available. Current plan allocations cannot be loaded from live data.",
      );
    }

    if (!configData) {
      warnings.push(
        "Company configuration is not available. Some allocation data may be incomplete.",
      );
    }

    return warnings;
  }, [
    yearId,
    totalBudget,
    budgetConfiguration,
    trainingCategoryBudgetConfig,
    trainingCategoryDefaults,
    setaTemplate,
    configData,
  ]);

  useEffect(() => {
    if (isInitialLoading) {
      setConfigurationWarnings([]);
      return;
    }
    setConfigurationWarnings(buildConfigurationWarnings());
  }, [isInitialLoading, buildConfigurationWarnings]);

  // Update T: values based on bbeeType (Budget or Headcount) and fetched configuration
  useEffect(() => {
    if (budgetConfiguration?.demographic_config?.config && totalBudget) {
      const config = budgetConfiguration.demographic_config.config;

      setBbeeValues((prevValues) => {
        const updatedValues = {};
        const isFirstLoad = !bbeeValuesInitialized;

        POPULATION_GROUPS.forEach((group) => {
          const existingConfig = config.find(
            (item) => item.race === group.key.toLowerCase()
          );

          if (existingConfig) {
            if (bbeeType === "Budget") {
              // Convert percentage values to actual budget values
              // Values from DB are percentages (e.g., 10 means 10%)
              const malePercentage = parseFloat(existingConfig.budget?.male || 0);
              const femalePercentage = parseFloat(existingConfig.budget?.female || 0);
              const tMale = (malePercentage / 100) * totalBudget;
              const tFemale = (femalePercentage / 100) * totalBudget;
              const prevT = prevValues[group.key]?.male?.t;
              const shouldResetC = !isFirstLoad && prevT !== undefined && prevT !== tMale;
              updatedValues[group.key] = {
                male: {
                  c: shouldResetC ? tMale : (prevValues[group.key]?.male?.c ?? 0),
                  t: tMale,
                },
                female: {
                  c: shouldResetC ? tFemale : (prevValues[group.key]?.female?.c ?? 0),
                  t: tFemale,
                },
              };
            } else {
              const tMale = Number(existingConfig.headCount?.male) || 0;
              const tFemale = Number(existingConfig.headCount?.female) || 0;
              const prevT = prevValues[group.key]?.male?.t;
              const shouldResetC = !isFirstLoad && prevT !== undefined && prevT !== tMale;
              updatedValues[group.key] = {
                male: {
                  c: shouldResetC ? tMale : (prevValues[group.key]?.male?.c ?? 0),
                  t: tMale,
                },
                female: {
                  c: shouldResetC ? tFemale : (prevValues[group.key]?.female?.c ?? 0),
                  t: tFemale,
                },
              };
            }
          } else {
            // Keep existing values if no config found
            updatedValues[group.key] = prevValues[group.key] || {
              male: { c: 0, t: 0 },
              female: { c: 0, t: 0 },
            };
          }
        });

        // Handle Disability separately (load from config.disability, not from config array)
        // Disability is handled separately with total only (no male/female split)

        // Mark as initialized after first load
        if (isFirstLoad) {
          setBbeeValuesInitialized(true);
        }

        return updatedValues;
      });
    }
  }, [budgetConfiguration, bbeeType, bbeeValuesInitialized, totalBudget]);

  // Load Unemployed T from demographic_config.none (absolute Rand)
  useEffect(() => {
    if (!budgetConfiguration?.demographic_config) return;
    const tValue =
      Number(budgetConfiguration.demographic_config?.none?.budget?.total) || 0;
    setUnemployedData((prev) => ({
      ...prev,
      t: tValue,
      c: prev.c ?? 0,
    }));
  }, [budgetConfiguration]);

  // Disability T is read from demographic_config.disability in the UI.
  // Disability C is populated from planned allocation only (same pattern as BBEE C).

  // Function to fetch and populate BBEE bifurcation data into C: values
  const fetchAndPopulateBBEEBifurcationData = async () => {
    if (!yearId || !companyId || !budgetConfiguration?.total_budget || !configData || !setaTemplate?.length) {
      setBbeeBifurcationLoaded(true);
      return;
    }

    try {
      // Extract setaFields from setaTemplate (same logic as Dashboard)
      // DEFAULT_SETA_FIELDS equivalent
      const DEFAULT_SETA_FIELDS = {
        costField: "cost",
        genderField: "gender",
        raceField: "race",
      };

      let fields = DEFAULT_SETA_FIELDS;

      if (configData?.training_need_workflow && configData?.select_training) {
        // Use default fields when workflow is enabled (same as Dashboard)
        // Don't extract from setaTemplate in this case
      } else {
        // Extract from setaTemplate (same logic as Dashboard)
        setaTemplate.forEach((item) => {
          if (item.config?.isTrainingCostField) {
            fields.costField = item.fieldName;
          }
          if (item.config?.isGenderField) {
            fields.genderField = item.fieldName;
          }
          if (item.config?.isRaceField) {
            fields.raceField = item.fieldName;
          }
        });
      }

      const setaFields = {
        costField: fields.costField || "cost",
        genderField: fields.genderField || "gender",
        raceField: fields.raceField || "race",
      };

      // Get user role and department info
      const departmentId = null; // For plan optimizer, we want all departments

      // Fetch BBEE bifurcation data
      const result = await dispatch(
        BBEE_bifurcation({
          company_id: companyId,
          yearId,
          department_id: departmentId,
          trainind_need_flow: configData?.training_need_workflow && configData?.select_training,
        })
      ).unwrap();

      const { config, allocated_data } = result;

      const employedAllocated = excludeUnemployedFromAllocated(
        allocated_data || [],
        employmentTypes,
      );
      const budgetData = getDemographicsBudgetData(
        config?.config || [],
        employedAllocated,
        setaFields,
      );

      // Populate C: values from bifurcation data (reset to 0 when no allocation data)
      setBbeeValues((prevValues) => {
        const updatedValues = { ...prevValues };

        POPULATION_GROUPS.forEach((group) => {
          updatedValues[group.key] = {
            male: { c: 0, t: prevValues[group.key]?.male?.t ?? 0 },
            female: { c: 0, t: prevValues[group.key]?.female?.t ?? 0 },
          };
        });

        (budgetData || []).forEach((item) => {
          // Parse demographic name (e.g., "African-Male" -> race: "african", gender: "male")
          const [race, gender] = item.name.split("-");
          if (!race || !gender) return;

          // Find matching population group
          const group = POPULATION_GROUPS.find(
            (g) => g.key.toLowerCase() === race.toLowerCase()
          );
          if (!group) return;

          // Update C: value based on bbeeType
          if (bbeeType === "Budget") {
            const cValue =
              (item.recommendedBudget || 0) + (item.approvedBudget || 0);
            if (!updatedValues[group.key]) {
              updatedValues[group.key] = {
                male: { c: 0, t: prevValues[group.key]?.male?.t || 0 },
                female: { c: 0, t: prevValues[group.key]?.female?.t || 0 },
              };
            }
            if (gender.toLowerCase() === "male") {
              updatedValues[group.key].male.c = cValue;
            } else if (gender.toLowerCase() === "female") {
              updatedValues[group.key].female.c = cValue;
            }
          } else {
            const cValue =
              (item.recommendedCount || 0) + (item.approvedCount || 0);
            if (!updatedValues[group.key]) {
              updatedValues[group.key] = {
                male: { c: 0, t: prevValues[group.key]?.male?.t || 0 },
                female: { c: 0, t: prevValues[group.key]?.female?.t || 0 },
              };
            }
            if (gender.toLowerCase() === "male") {
              updatedValues[group.key].male.c = cValue;
            } else if (gender.toLowerCase() === "female") {
              updatedValues[group.key].female.c = cValue;
            }
          }
        });

        return updatedValues;
      });
    } catch (error) {
      console.error("Error fetching BBEE bifurcation data:", error);
    } finally {
      setBbeeBifurcationLoaded(true);
    }
  };

  // Populate Unemployed C from planned spend (employment type + stipend)
  const fetchAndPopulateUnemployedBudgetData = async () => {
    if (
      !showUnemployedSlider ||
      !yearId ||
      !companyId ||
      !configData ||
      !setaTemplate?.length
    ) {
      return;
    }

    try {
      const DEFAULT_SETA_FIELDS = { costField: "cost" };
      let fields = { ...DEFAULT_SETA_FIELDS };
      if (!(configData?.training_need_workflow && configData?.select_training)) {
        setaTemplate.forEach((item) => {
          if (item.config?.isTrainingCostField) {
            fields.costField = item.fieldName;
          }
        });
      }
      const setaFields = { costField: fields.costField || "cost" };

      const result = await dispatch(
        fetchDepartmentBudgetDetails({
          isTrainingEnabled:
            configData?.training_need_workflow && configData?.select_training,
          company_id: companyId,
          department_id: null,
          yearId,
        }),
      );

      const allocated = result?.payload?.allocated_data || [];
      const planned = computeUnemployedRecommendedInvestment(
        allocated,
        employmentTypes,
        setaFields,
      );

      setUnemployedData((prev) => ({
        ...prev,
        c: planned,
      }));
    } catch (error) {
      console.error("Error fetching unemployed budget data:", error);
    }
  };

  // Populate Disability C from planned allocation (recommended + approved), same as BBEE C.
  // T remains the configured disability budget target shown in the UI.
  const fetchAndPopulateDisabilityBudgetData = async () => {
    if (!yearId || !companyId || !configData || !setaTemplate?.length) {
      return;
    }

    try {
      // Extract setaFields from setaTemplate (same logic as Dashboard)
      const DEFAULT_SETA_FIELDS = {
        costField: "cost",
        genderField: "gender",
        raceField: "race",
      };

      let fields = DEFAULT_SETA_FIELDS;

      if (configData?.training_need_workflow && configData?.select_training) {
        // Use default fields when workflow is enabled
      } else {
        // Extract from setaTemplate
        setaTemplate.forEach((item) => {
          if (item.config?.isTrainingCostField) {
            fields.costField = item.fieldName;
          }
          if (item.config?.isGenderField) {
            fields.genderField = item.fieldName;
          }
          if (item.config?.isRaceField) {
            fields.raceField = item.fieldName;
          }
        });
      }

      const setaFields = {
        costField: fields.costField || "cost",
        genderField: fields.genderField || "gender",
        raceField: fields.raceField || "race",
      };

      // Plan optimizer uses all departments (same as BBEE bifurcation)
      const departmentId = null;

      const result = await dispatch(
        fetchDepartmentBudgetDetailsDisabled({
          company_id: companyId,
          department_id: departmentId,
          yearId,
        })
      );

      const data = result?.payload || [];

      let totalPlanned = 0;

      const isPlannedStatus = (status) =>
        status === trainingFormStatus.RECOMMENDED ||
        status === trainingFormStatus.APPROVED;

      data?.forEach((item) => {
        if (!isPlannedStatus(item.status)) return;
        const cost = Number(item[setaFields.costField] ?? item.cost) || 0;
        totalPlanned += cost;
      });

      // Always set C from planned spend (including 0), never from config target
      if (bbeeType === "Budget") {
        setDisabilityData({
          total: totalPlanned.toString(),
        });
      }
    } catch (error) {
      console.error("Error fetching disability budget data:", error);
    }
  };

  // Fetch and populate BBEE bifurcation data when dependencies are ready
  useEffect(() => {
    if (!open || !eligibilityChecked || !bbeeValuesInitialized) {
      return;
    }

    const canFetch =
      yearId &&
      companyId &&
      budgetConfiguration?.total_budget &&
      configData &&
      setaTemplate?.length;

    if (canFetch) {
      fetchAndPopulateBBEEBifurcationData();
    } else {
      setBbeeBifurcationLoaded(true);
    }
  }, [
    open,
    eligibilityChecked,
    yearId,
    companyId,
    budgetConfiguration?.total_budget,
    configData,
    setaTemplate,
    bbeeType,
    bbeeValuesInitialized,
  ]);

  // Function to fetch and populate Training Category Budget data into C: values
  // Memoized with useCallback to prevent unnecessary re-renders
  const fetchAndPopulateTrainingCategoryBudgetData = useCallback(async () => {
    if (!yearId || !companyId || !configData || !setaTemplate?.length) {
      setTrainingCategoryBudgetLoaded(true);
      return;
    }

    try {
      // Extract setaFields from setaTemplate (same logic as Dashboard)
      const DEFAULT_SETA_FIELDS = {
        costField: "cost",
        trainingCategory: "training_category",
      };

      let fields = DEFAULT_SETA_FIELDS;

      if (configData?.training_need_workflow && configData?.select_training) {
        // Use default fields when workflow is enabled
      } else {
        // Extract from setaTemplate
        setaTemplate.forEach((item) => {
          if (item.config?.isTrainingCostField) {
            fields.costField = item.fieldName;
          }
          if (item.config?.isTrainingCategoryField) {
            fields.trainingCategory = item.fieldName;
          }
        });
      }

      const setaFields = {
        costField: fields.costField || "cost",
        trainingCategory: fields.trainingCategory || "training_category",
      };

      // Get user role and department info
      const departmentId = null; // For plan optimizer, we want all departments

      // Fetch category config
      const categoryConfigRes = await dispatch(
        fetchTrainingCategoryConfig({ yearId, companyId })
      ).unwrap();

      const categoryConfig = categoryConfigRes || [];

      // Fetch allocated data using fetchTrainingCategoryBudgetConfig action
      const result = await dispatch(
        fetchTrainingCategoryBudgetConfig({
          company_id: companyId,
          yearId,
          department_id: departmentId,
          training_need_workflow: configData?.training_need_workflow && configData?.select_training,
        })
      ).unwrap();

      const { allocated_data } = result || {};

      // Ensure employment_type / stipend on rows (RPC may lag migration; keep Optimiser C aligned)
      let allocatedWithEmployment = allocated_data || [];
      const needsEmploymentEnrichment =
        showUnemployedSlider &&
        allocatedWithEmployment.some(
          (r) => r?.employment_type == null || r.employment_type === "",
        );
      if (needsEmploymentEnrichment && companyId) {
        const empIds = [
          ...new Set(
            allocatedWithEmployment
              .map((r) => r?.emp_id)
              .filter((id) => id != null && id !== ""),
          ),
        ];
        if (empIds.length) {
          const { data: users } = await supabase
            .from("company_user")
            .select("id, emp_id, employment_type, stipend_amount")
            .eq("company_id", companyId)
            .eq("status", "active");
          const byEmpId = new Map();
          const byUuid = new Map();
          (users || []).forEach((u) => {
            if (u.emp_id != null) byEmpId.set(String(u.emp_id), u);
            if (u.id != null) byUuid.set(String(u.id), u);
          });
          allocatedWithEmployment = allocatedWithEmployment.map((row) => {
            const key = String(row.emp_id ?? "");
            const u = byEmpId.get(key) || byUuid.get(key);
            if (!u) return row;
            return {
              ...row,
              employment_type: row.employment_type || u.employment_type || "",
              stipend_amount:
                row.stipend_amount != null && row.stipend_amount !== ""
                  ? row.stipend_amount
                  : u.stipend_amount ?? 0,
            };
          });
        }
      }

      const budgetAnalysis = getTrainingCategoryBudgetUsage(
        categoryConfig,
        allocatedWithEmployment,
        setaFields,
        employmentTypes,
      );

      setTrainingCategoryValues((prevValues) => {
        const updatedValues = { ...prevValues };

        TRAINING_CATEGORY_OPTIONS.forEach((cat) => {
          updatedValues[cat.value] = {
            c: 0,
            t: prevValues[cat.value]?.t ?? 0,
          };
        });

        (budgetAnalysis || []).forEach((item) => {
          const cValue =
            (item.recommendedTrainingsBudget || 0) +
            (item.approvedTrainingsBudget || 0);
          const tValue = item.totalBudget || 0;

          if (updatedValues[item.category]) {
            updatedValues[item.category] = {
              c: cValue,
              t: tValue || prevValues[item.category]?.t || 0,
            };
          }
        });

        return updatedValues;
      });
    } catch (error) {
      console.error("Error fetching Training Category Budget data:", error);
    } finally {
      setTrainingCategoryBudgetLoaded(true);
    }
  }, [yearId, companyId, configData, setaTemplate, dispatch, employmentTypes, showUnemployedSlider]);

  // Fetch and populate Disability + Unemployed C from planned allocation when dependencies are ready
  useEffect(() => {
    if (!open || !eligibilityChecked) {
      return;
    }
    if (yearId && companyId && configData && setaTemplate?.length && bbeeType === "Budget") {
      fetchAndPopulateDisabilityBudgetData();
      fetchAndPopulateUnemployedBudgetData();
    }
  }, [open, eligibilityChecked, yearId, companyId, configData, setaTemplate, bbeeType, employmentTypes, showUnemployedSlider]);

  // Fetch and populate Training Category Budget data when dependencies are ready
  useEffect(() => {
    if (!open || !eligibilityChecked || !trainingCategoryValuesInitialized) {
      return;
    }

    const canFetch =
      yearId &&
      companyId &&
      configData &&
      setaTemplate?.length;

    if (canFetch) {
      fetchAndPopulateTrainingCategoryBudgetData();
    } else {
      setTrainingCategoryBudgetLoaded(true);
    }
  }, [
    open,
    eligibilityChecked,
    yearId,
    companyId,
    configData,
    setaTemplate,
    trainingCategoryValuesInitialized,
    fetchAndPopulateTrainingCategoryBudgetData,
  ]);

  // Note: Training Category Budget data is now loaded when clicking Generate button
  // This ensures fresh data from Dashboard function is used

  // Calculate current budget for BBEE section (race-gender C + unemployed C)
  const calculateBBEECurrentBudget = () => {
    if (bbeeType !== "Budget") {
      return 0; // Only calculate budget when bbeeType is "Budget"
    }
    let total = 0;
    POPULATION_GROUPS.forEach((group) => {
      const maleC = bbeeValues[group.key]?.male?.c ?? 0;
      const femaleC = bbeeValues[group.key]?.female?.c ?? 0;
      total += Number(maleC) + Number(femaleC);
    });
    if (showUnemployedSlider) {
      total += Number(unemployedData?.c ?? 0) || 0;
    }
    return total;
  };

  const calculateBlackTotal = () => {
    const blackGroups = POPULATION_GROUPS.filter((group) => group.category === "black");
    let total = 0;
    blackGroups.forEach((group) => {
      const maleC = bbeeValues[group.key]?.male?.c ?? 0;
      const femaleC = bbeeValues[group.key]?.female?.c ?? 0;
      total += Number(maleC) + Number(femaleC);
    });
    return total;
  };

  const calculateOtherTotal = () => {
    const otherGroups = POPULATION_GROUPS.filter((group) => group.category === "other");
    let total = 0;
    otherGroups.forEach((group) => {
      const maleC = bbeeValues[group.key]?.male?.c ?? 0;
      const femaleC = bbeeValues[group.key]?.female?.c ?? 0;
      total += Number(maleC) + Number(femaleC);
    });
    return total;
  };

  // Calculate current budget for Training Category section (sum of all C: values)
  const calculateTrainingCategoryCurrentBudget = () => {
    let total = 0;
    TRAINING_CATEGORY_OPTIONS.forEach((category) => {
      const cValue = trainingCategoryValues[category.value]?.c ?? 0;
      total += Number(cValue);
    });
    return total;
  };

  const bbeeCurrentBudget = calculateBBEECurrentBudget();
  const trainingCategoryCurrentBudget = calculateTrainingCategoryCurrentBudget();
  const sectionsAligned = bbeeCurrentBudget === trainingCategoryCurrentBudget;

  const handleAutoAlignBbee = (groupKey, gender) => {
    const sourceTotal = calculateBBEECurrentBudget();
    const otherTotal = calculateTrainingCategoryCurrentBudget();
    const currentValue = Number(bbeeValues[groupKey]?.[gender]?.c ?? 0);
    const newValue = computeAutoAlignedSliderValue({
      currentValue,
      sourceSectionTotal: sourceTotal,
      otherSectionTotal: otherTotal,
 
    });
    setBbeeValues((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [gender]: { ...prev[groupKey]?.[gender], c: newValue },
      },
    }));
  };

  const handleAutoAlignTraining = (categoryValue) => {
    const sourceTotal = calculateTrainingCategoryCurrentBudget();
    const otherTotal = calculateBBEECurrentBudget();
    const currentValue = Number(trainingCategoryValues[categoryValue]?.c ?? 0);
    const newValue = computeAutoAlignedSliderValue({
      currentValue,
      sourceSectionTotal: sourceTotal,
      otherSectionTotal: otherTotal,

    });
    setTrainingCategoryValues((prev) => ({
      ...prev,
      [categoryValue]: {
        ...prev[categoryValue],
        c: newValue,
      },
    }));
  };

  const configuredTotalBudget = Number(totalBudget) || 0;
  const hasValidBudget =
    configuredTotalBudget > 0 && calculateBBEECurrentBudget() > 0;

  const handleGenerate = async () => {
    // Prevent multiple submissions
    if (loading) {
      return;
    }

    if (configuredTotalBudget <= 0) {
      toast.error(
        "Total training budget must be greater than zero before generating a plan.",
      );
      return;
    }

    // Validation: Check if BBEE current budget matches Training Category current budget
    const bbeeCurrentBudget = calculateBBEECurrentBudget();
    const trainingCategoryCurrentBudget = calculateTrainingCategoryCurrentBudget();

    if (bbeeCurrentBudget <= 0) {
      toast.error(
        "Current budget must be greater than zero before generating a plan.",
      );
      return;
    }
    
    if (bbeeCurrentBudget !== trainingCategoryCurrentBudget) {
      toast.error(
        `Budget mismatch: BBEE Current Budget (${formatZAR(bbeeCurrentBudget)}) must equal Training Category Current Budget (${formatZAR(trainingCategoryCurrentBudget)}). Please adjust the values to match.`
      );
      return;
    }

    try {
      setLoading(true);

      // Build the optimized payload
      const optimizedPayload = await buildPlanOptimizerPayload({
        companyId,
        yearId,
        bbeeValues,
        trainingCategoryValues,
        totalBudget: calculateBBEECurrentBudget(),
        bbeeType,
        trainingCategoryType,
        criticalSkillsPriority,
        calculateBBEECurrentBudget,
        calculateTrainingCategoryCurrentBudget,
        disabilityData, // Pass Disability data (total only)
        unemployedData: showUnemployedSlider
          ? { total: String(unemployedData?.c ?? 0) }
          : { total: "0" },
      });

      const expandedPayload = optimizedPayload._expandedPayload || optimizedPayload;
      const sliderMeta = {
        criticalSkillsPriority,
        bbeeValues,
        trainingCategoryValues,
        disabilityData,
        unemployedData,
        bbeeType,
        trainingCategoryType,
      };
      const setaFieldNames = listSetaFieldNamesFromTemplate(setaTemplate);

      const artifact = await runPlanOptimizerV2Generate({
        companyId,
        expandedPayload,
        sliderMeta,
        setaFieldNames,
        yearId,
      });

      const generatedPlan = artifact.generated_plan;
      // Currents must use per-employee cost/category (server may still key by training_id alone).
      const allocationSummary =
        recomputeAllocationSummary(expandedPayload, generatedPlan, sliderMeta) ||
        artifact.allocation_summary;
      const artifactWithSummary = {
        ...artifact,
        allocation_summary: allocationSummary,
      };
      setCompletePlanArtifact(artifactWithSummary);
      setGeneratedPlanData(artifact.generated_plan);
      setOptimizedPayloadData(optimizedPayload);
      setPrecomputedSummary(allocationSummary);
      setAllocationSummaryData(allocationSummary);

      // Close this modal and open the allocation summary modal
      onClose();
      setIsAllocationSummaryModalOpen(true);

      // Call onGenerate callback if provided
      if (onGenerate) {
        onGenerate({
          generated_plan: generatedPlan,
          optimized_payload: optimizedPayload,
          bbeeValues,
          trainingCategoryValues,
          bbeeType,
          trainingCategoryType,
          criticalSkillsPriority,
        });
      }
    } catch (error) {
      console.error("Error in handleGenerate:", error);
      toast.error("Error generating plan: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReconfigure = () => {
    setIsAllocationSummaryModalOpen(false);
    // Pass values back to parent for regenerate
    if (onGenerate) {
      onGenerate({
        regenerate: true,
        bbeeValues,
        trainingCategoryValues,
        bbeeType,
        trainingCategoryType,
        criticalSkillsPriority,
      });
    }
  };

  const handleSave = async () => {
    // Prevent multiple submissions
    if (loading) {
      return;
    }

    if (!generatedPlanData || !optimizedPayloadData) {
      toast.error("No data to save. Please generate a plan first.");
      return;
    }

    try {
      setLoading(true);
      const userId = getLocalData("sdfUser")?.id;

      if (!completePlanArtifact) {
        toast.error("No complete plan artifact to save. Please generate a plan first.");
        return;
      }

      await savePlanOptimizerV2Artifact({
        companyId,
        yearId,
        createdBy: userId,
        compactPayload: optimizedPayloadData,
        artifact: completePlanArtifact,
      });
      toast.success("Plan saved successfully!");

      // Remove focus from any focused elements before closing modal
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }

      setIsAllocationSummaryModalOpen(false);
      setCompletePlanArtifact(null);
      setPrecomputedSummary(null);

      // Call onGenerate callback to refresh history in parent
      if (onGenerate) {
        onGenerate({
          saved: true,
        });
      }
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("Error saving plan: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CDialog
        open={open}
        onClose={onClose}
        className="plan-optimizer-dialog"
        maxWidth={false}
      >
        <div className="plan-optimizer-shell w-[min(96vw,1400px)] max-w-[1400px] flex flex-col max-h-[calc(100vh-32px)]">
          {/* Modal Header */}
          <div className="plan-optimizer-header flex items-center justify-between shrink-0 px-3 pt-3 pb-2">
            <h2 className="text-xl font-semibold text-blue-500">Plan Optimizer</h2>
            <CButton
              onClick={onClose}
              className="!p-0 !min-w-0"
            >
              <CloseIcon className="text-gray-700 hover:text-gray-900" />
            </CButton>
          </div>

          <div className="plan-optimizer-body flex-1 min-h-0 overflow-auto border-t border-blue-100 px-3 py-3">
            {isInitialLoading ? (
              <div className="min-h-[420px] flex items-center justify-center">
                <CLoader size="large" local={true} />
              </div>
            ) : (
              <>
            {configurationWarnings.length > 0 && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  Configuration incomplete
                </p>
                <p className="text-xs text-amber-800 mb-2">
                  You can still adjust values below, but some budget targets and
                  current allocations may show as zero until configuration is completed.
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  {configurationWarnings.map((warning) => (
                    <li key={warning} className="text-xs text-amber-900">
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* One responsive grid: stack below xl (tablet); side-by-side on desktop */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-1">
              {/* Left: B-BBEE + Disability */}
              <div className="min-w-0 flex flex-col gap-3">
              <div className="p-0 min-w-0">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">
                  B-BBEE Budget Adjustment
                </h3>

                {/* Table Structure */}
                <div className="overflow-x-auto rounded-lg border border-solid border-blue-200">
                  <table className="w-full min-w-[640px] border-collapse custom-table table-fixed">
                    <colgroup>
                      <col className="w-[16%]" />
                      <col className="w-[42%]" />
                      <col className="w-[42%]" />
                    </colgroup>
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="text-left border-b border-solid border-blue-200 font-semibold text-[12px] leading-sm bg-blue-100 p-2 text-gray-800" colSpan="3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            {/* Radio buttons hidden - BBEE type is always "Budget" */}
                            <div className="flex-shrink-0">
                              <span className="text-sm font-semibold text-gray-800">Budget</span>
                            </div>
                            <div className="text-right justify-end sm:mr-2">
                              <span className="text-[13px] sm:text-[14px] text-gray-600">Budget: {formatZAR(totalBudget || 0)}</span>
                              <br />
                              <span className="text-[13px] sm:text-[14px] text-gray-600">Current Plan: {formatZAR(calculateBBEECurrentBudget())}</span>
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="mb-1">
                        <td></td>
                        <td className="text-center p-2 text-[14px] leading-sm font-medium text-gray-700">Male</td>
                        <td className="text-center p-2 text-[14px] leading-sm font-medium text-gray-700">Female</td>
                      </tr>
                      {POPULATION_GROUPS.map((group, groupIndex) => (
                        <tr key={group.key} className="mb-1">
                          <td className="text-left p-2 text-[14px] leading-sm font-medium text-gray-700 whitespace-nowrap">{group.label}</td>
                          {/* Male Column */}
                          <td className="p-1 align-top">
                            <BudgetControlCell
                              value={Number(bbeeValues[group.key]?.male?.c ?? 0)}
                              onChange={(value) => {
                                setBbeeValues((prev) => ({
                                  ...prev,
                                  [group.key]: {
                                    ...prev[group.key],
                                    male: { ...prev[group.key].male, c: value },
                                  },
                                }));
                              }}
                              max={bbeeType === "Budget" ? (configuredTotalBudget || 500) : 500}
                              targetDisplay={
                                bbeeType === "Budget"
                                  ? formatZAR(bbeeValues[group.key]?.male?.t || 0)
                                  : (bbeeValues[group.key]?.male?.t || 0)
                              }
                              showCurrencyPrefix={bbeeType === "Budget"}
                              disabled={loading}
                              tabIndex={groupIndex * 2 + 1}
                              onAutoAlign={() => handleAutoAlignBbee(group.key, "male")}
                              autoAlignDisabled={sectionsAligned || loading}
                            />
                          </td>
                          {/* Female Column */}
                          <td className="p-1 align-top">
                            <BudgetControlCell
                              value={Number(bbeeValues[group.key]?.female?.c ?? 0)}
                              onChange={(value) => {
                                setBbeeValues((prev) => ({
                                  ...prev,
                                  [group.key]: {
                                    ...prev[group.key],
                                    female: { ...prev[group.key].female, c: value },
                                  },
                                }));
                              }}
                              max={bbeeType === "Budget" ? (configuredTotalBudget || 500) : 500}
                              targetDisplay={
                                bbeeType === "Budget"
                                  ? formatZAR(bbeeValues[group.key]?.female?.t || 0)
                                  : (bbeeValues[group.key]?.female?.t || 0)
                              }
                              showCurrencyPrefix={bbeeType === "Budget"}
                              disabled={loading}
                              tabIndex={groupIndex * 2 + 2}
                              onAutoAlign={() => handleAutoAlignBbee(group.key, "female")}
                              autoAlignDisabled={sectionsAligned || loading}
                            />
                          </td>
                        </tr>
                      ))}
                      {showUnemployedSlider && (
                        <tr className="mb-1 border-t border-blue-100">
                          <td className="text-left p-2 text-[14px] leading-sm font-medium text-gray-700 whitespace-nowrap align-top">
                            Unemployed
                          </td>
                          <td className="p-1 align-top" colSpan="2">
                            <div className="max-w-[320px]">
                              <BudgetControlCell
                                value={Number(unemployedData?.c ?? 0)}
                                onChange={(value) => {
                                  setUnemployedData((prev) => ({
                                    ...prev,
                                    c: value,
                                  }));
                                }}
                                max={
                                  bbeeType === "Budget"
                                    ? configuredTotalBudget || 500
                                    : 500
                                }
                                targetDisplay={formatZAR(unemployedData?.t || 0)}
                                disabled={loading}
                                tabIndex={POPULATION_GROUPS.length * 2 + 1}
                                onAutoAlign={() => {
                                  const sourceTotal = calculateBBEECurrentBudget();
                                  const otherTotal =
                                    calculateTrainingCategoryCurrentBudget();
                                  const currentValue = Number(unemployedData?.c ?? 0);
                                  const newValue = computeAutoAlignedSliderValue({
                                    currentValue,
                                    sourceSectionTotal: sourceTotal,
                                    otherSectionTotal: otherTotal,
                                  });
                                  setUnemployedData((prev) => ({
                                    ...prev,
                                    c: newValue,
                                  }));
                                }}
                                autoAlignDisabled={sectionsAligned || loading}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <div className="flex-1 bg-gray-50 border border-blue-200 rounded-lg p-2 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-800">Black Total</span>
                      <span className="text-base font-semibold text-gray-700">
                        {bbeeType === "Budget" ? formatZAR(calculateBlackTotal()) : calculateBlackTotal()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {POPULATION_GROUPS.filter((group) => group.category === "black").map((group) => group.label).join(" + ")}
                    </div>
                  </div>
                  <div className="flex-1 bg-gray-50 border border-blue-200 rounded-lg p-2 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-800">Other Total</span>
                      <span className="text-base font-semibold text-gray-700">
                        {bbeeType === "Budget" ? formatZAR(calculateOtherTotal()) : calculateOtherTotal()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {POPULATION_GROUPS.filter((group) => group.category === "other").map((group) => group.label).join(" + ")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Disability under B-BBEE (same column) */}
              <div className="min-w-0">
                <div className="overflow-x-auto rounded-lg border border-solid border-blue-200">
                  <table className="w-full border-collapse custom-table">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="text-left border-b border-solid border-blue-200 font-semibold text-[12px] leading-sm bg-blue-100 p-2 text-gray-800" colSpan="2">
                          <span className="text-sm font-semibold text-gray-800">Disability Budget</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-left p-1 pr-2 text-[14px] leading-sm font-medium text-gray-700 w-24 whitespace-nowrap">Budget</td>
                        <td className="p-1 align-top">
                          <div className="max-w-[320px]">
                            <BudgetControlCell
                              value={parseFloat(disabilityData.total) || 0}
                              onChange={(value) => {
                                setDisabilityData((prev) => ({
                                  ...prev,
                                  total: String(value),
                                }));
                              }}
                              max={bbeeType === "Budget" ? (configuredTotalBudget || 500) : 500}
                              targetDisplay={
                                bbeeType === "Budget"
                                  ? formatZAR(
                                      budgetConfiguration?.demographic_config
                                        ?.disability?.budget?.total || 0,
                                    )
                                  : (budgetConfiguration?.demographic_config
                                      ?.disability?.head_count?.total || 0)
                              }
                              disabled={loading}
                              tabIndex={
                                POPULATION_GROUPS.length * 2 +
                                (showUnemployedSlider ? 1 : 0) +
                                trainingCategories.length +
                                1
                              }
                              showAutoAlign={false}
                            />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              </div>

              {/* Right: Training Category + Critical Skills (stacks below left on tablet) */}
              <div className="min-w-0 flex flex-col gap-3">
              <div className="p-0 min-w-0">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">
                  Training Category
                </h3>

                {/* Table Structure */}
                <div className="overflow-x-auto rounded-lg border border-solid border-blue-200">
                  <table className="w-full min-w-[420px] border-collapse custom-table">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="p-2 text-left border-b border-solid border-blue-200 font-semibold text-[12px] leading-sm bg-blue-100 text-gray-800" colSpan="2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-800">Budget</span>
                            <div className="text-right">
                              <span className="text-[13px] sm:text-[14px] text-gray-600">Budget: {formatZAR(totalBudget || 0)}</span>
                              <br />
                              <span className="text-[13px] sm:text-[14px] text-gray-600">Current Plan: {formatZAR(calculateTrainingCategoryCurrentBudget())}</span>
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainingCategories.map((category, categoryIndex) => {
                        const trainingTabIndex =
                          POPULATION_GROUPS.length * 2 +
                          (showUnemployedSlider ? 1 : 0) +
                          categoryIndex +
                          1;
                        return (
                        <tr key={category.value} className="mb-1">
                          <td className="text-left p-2 text-[14px] leading-sm font-medium text-gray-700 whitespace-nowrap w-[30%] max-w-[9rem]">
                            {category.value}
                          </td>
                          <td className="p-1 align-top">
                            <BudgetControlCell
                              value={Number(trainingCategoryValues[category.value]?.c ?? 0)}
                              onChange={(value) => {
                                setTrainingCategoryValues((prev) => ({
                                  ...prev,
                                  [category.value]: {
                                    ...prev[category.value],
                                    c: value,
                                  },
                                }));
                              }}
                              max={configuredTotalBudget || 500}
                              targetDisplay={formatZAR(
                                trainingCategoryValues[category.value]?.t || 0,
                              )}
                              disabled={loading}
                              tabIndex={trainingTabIndex}
                              onAutoAlign={() =>
                                handleAutoAlignTraining(category.value)
                              }
                              autoAlignDisabled={sectionsAligned || loading}
                            />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="min-w-0">
                <div className="overflow-x-auto rounded-lg border border-solid border-blue-200">
                  <table className="w-full border-collapse custom-table">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="text-left border-b border-solid border-blue-200 font-semibold text-[12px] leading-sm bg-blue-100 p-2 text-gray-800" colSpan="1">
                          <span className="text-sm font-semibold text-gray-800">Critical Skills Priority</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div className="px-3 py-3 relative min-h-[80px] flex items-center">
                            <Slider
                              tabIndex={POPULATION_GROUPS.length * 2 + trainingCategories.length + 2}
                              disabled={loading}
                              value={Number(criticalSkillsPriority) || 0}
                              onChange={(e, newValue) => setCriticalSkillsPriority(newValue)}
                              min={0}
                              max={5}
                              step={1}
                              valueLabelDisplay="on"
                              marks={[
                                { value: 0, label: '0' },
                                { value: 1, label: '1' },
                                { value: 2, label: '2' },
                                { value: 3, label: '3' },
                                { value: 4, label: '4' },
                                { value: 5, label: '5' },
                              ]}
                              className="w-full"
                              sx={{
                                '& .MuiSlider-valueLabel': {
                                  backgroundColor: '#008cd1',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                },
                                '& .MuiSlider-thumb': {
                                  width: 16,
                                  height: 16,
                                  backgroundColor: '#ffffff',
                                  border: '2px solid #008cd1',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    boxShadow: 'none',
                                  },
                                  '&.Mui-focusVisible': {
                                    boxShadow: 'none',
                                  },
                                  '&.Mui-active': {
                                    boxShadow: 'none',
                                  },
                                },
                                '& .MuiSlider-track': {
                                  height: 3,
                                  backgroundColor: '#008cd1',
                                  border: 'none',
                                },
                                '& .MuiSlider-rail': {
                                  height: 3,
                                  backgroundColor: '#e5e5e5',
                                  opacity: 1,
                                },
                                '& .MuiSlider-mark': {
                                  width: 2,
                                  height: 6,
                                  backgroundColor: '#e5e5e5',
                                },
                                '& .MuiSlider-markLabel': {
                                  fontSize: '9px',
                                  color: '#505050',
                                  marginTop: '4px',
                                },
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </div>

              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="plan-optimizer-footer flex justify-center gap-2 shrink-0 px-3 py-3 border-t border-blue-100 flex-wrap">
            <CButton
              onClick={handleGenerate}
              disabled={loading || isInitialLoading || !hasValidBudget}
              title={
                !hasValidBudget
                  ? "Set a budget greater than zero to generate a plan"
                  : undefined
              }
              className="bg-blue-500 text-white hover:bg-blue-600 border border-blue-500 rounded-lg px-6 py-2 w-[9.5rem] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Generating" : "Generate"}
            </CButton>
            <CButton
              onClick={onClose}
              className="bg-white text-blue-500 hover:bg-blue-50 border border-blue-500 rounded-lg px-6 py-2"
            >
              Cancel
            </CButton>
          </div>
        </div>
      </CDialog>

      {/* Allocation Summary Modal */}
      <AllocationSummaryModal
        open={isAllocationSummaryModalOpen}
        onClose={() => {
          setIsAllocationSummaryModalOpen(false);
          setGeneratedPlanData(null);
          setOptimizedPayloadData(null);
          setAllocationSummaryData(null);
          setCompletePlanArtifact(null);
          setPrecomputedSummary(null);
        }}
        onReconfigure={handleReconfigure}
        onSave={handleSave}
        generatedPlan={generatedPlanData}
        inputData={optimizedPayloadData}
        loading={loading}
        storedSummary={precomputedSummary}
        // Pass slider configuration values for target values
        bbeeValues={bbeeValues}
        trainingCategoryValues={trainingCategoryValues}
        disabilityData={disabilityData}
        unemployedData={unemployedData}
        bbeeType={bbeeType}
        trainingCategoryType={trainingCategoryType}
        totalBudget={calculateBBEECurrentBudget()}
        onSummaryCalculated={setAllocationSummaryData}
        criticalSkillsPriority={criticalSkillsPriority}
      />
    </>
  );
};

export default PlanOptimizerModal;

